import { useQuery } from "@tanstack/react-query";
import { getConversationList } from "@/services/message/message.service";
import type {
  GetConversationListRequest,
  ContractorConversationItem,
} from "@/interface/message.types";
import { useChatUnreadStore } from "@/store/chat-unread-store";

/**
 * React Query hook to fetch and manage conversation list for contractors
 * Transforms API response to match contractor UI component needs
 *
 * @param isLoggedIn - Whether user is logged in (enables/disables query)
 * @param request - Optional request parameters for pagination, filtering, etc.
 * @returns React Query result with transformed conversation list data
 */
export const useContractorConversationList = (
  isLoggedIn: boolean | null,
  request?: Partial<GetConversationListRequest>
) => {
  const isUnread = useChatUnreadStore((state) => state.isUnread);

  return useQuery({
    queryKey: ["conversations", "Contractor", request],
    queryFn: () => getConversationList(request),
    enabled: !!isLoggedIn,
    select: (response): ContractorConversationItem[] => {
      // Transform API response to contractor UI component format
      // For contractor view: userDetail contains homeowner information
      return response.data.coversationData.map((item) => ({
        id: item.proposalId, // Use proposalId as unique identifier
        homeownerName: item.userDetail.username, // Homeowner's name (contractor sees homeowner)
        homeownerId: item.userDetail.id, // Homeowner's ID
        projectTitle: item.projectTitle,
        projectId: item.projectId,
        proposalId: item.proposalId,
        // Fallback to projectTitle if lastMessage is empty
        message: item.lastMessage || item.projectTitle,
        timestamp: item.messagedDay, // Relative time already formatted
        rawTimestamp: item.messagedDay, // Use as proxy if raw ISO isn't available
        isUnread: isUnread(item.proposalId), // Merge persisted unread state
      }));
    },
    refetchOnWindowFocus: true,
    retry: 2,
  });
};

